import { useEffect, useMemo, useState } from "react";
import { getRoles, GetRolesParams, RoleSummary } from "../roles";
import { getApiErrorMessage } from "../../lib";
import { PaginationMeta } from "../../lib/bufferedPagination";

type UseRolesState = {
  roles: RoleSummary[];
  meta?: PaginationMeta;
  isLoading: boolean;
  error: string | null;
};

const rolesCache = new Map<string, { items: RoleSummary[]; meta?: PaginationMeta }>();

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setFullYear(endDate.getFullYear() - 2);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

export const useRoles = (params?: Partial<GetRolesParams>) => {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const requestParams: GetRolesParams = useMemo(() => ({
    startDate: params?.startDate ?? defaultRange.startDate,
    endDate: params?.endDate ?? defaultRange.endDate,
    limit: params?.limit ?? 100,
    sort: params?.sort ?? "a-z",
    q: params?.q ?? "",
    page: params?.page ?? 1,
  }), [
    defaultRange.endDate,
    defaultRange.startDate,
    params?.endDate,
    params?.limit,
    params?.page,
    params?.q,
    params?.sort,
    params?.startDate,
  ]);

  const [state, setState] = useState<UseRolesState>({
    roles: [],
    meta: undefined,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const requestKey = JSON.stringify(requestParams);
      const cached = rolesCache.get(requestKey);
      setState((prev) => ({
        roles: cached?.items ?? prev.roles,
        meta: cached?.meta ?? prev.meta,
        isLoading: cached ? false : true,
        error: null,
      }));
      try {
        const rolesResult = await getRoles(requestParams);
        if (!isMounted) return;
        rolesCache.set(requestKey, rolesResult);
        setState({
          roles: rolesResult.items,
          meta: rolesResult.meta,
          isLoading: false,
          error: null,
        });

        const nextParams = {
          ...requestParams,
          page: (requestParams.page ?? 1) + 1,
        };
        const nextKey = JSON.stringify(nextParams);
        if (!rolesCache.has(nextKey)) {
          getRoles(nextParams)
            .then((nextResult) => {
              rolesCache.set(nextKey, nextResult);
            })
            .catch(() => {});
        }
      } catch (error) {
        if (!isMounted) return;
        const message = getApiErrorMessage(error, "Unable to load roles");
        setState({ roles: [], meta: undefined, isLoading: false, error: message });
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [requestParams, refreshCount]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
